<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div ref="wyTableList" class="wy-table-list">
    <div :class="(tableData && tableData.length > 0 && iShowTable? 'wy-table-wrapper':'wy-table-wrapper no-data-table-wrapper')" style="width: 100%; overflow-x: hidden;">
      <div v-if="tableData && tableData.length > 0 && iShowTable">
        <!-- Main columns table -->
        <table border="0" class="wy-table-main">
          <thead>          
            <tr v-if="!noMainColumn" ref="wyHeaderMain" class="wy-header">
              <th v-for="(group, index) in groupColumns" :key="index" :colspan="group.headerSpan" :class="group.customClass" 
                  v-bind:style="{'min-width': (group.mainColumn?variableColWidth:group.headerWith)+'px !important'}">
                <span v-if="group.type!='index' && group.type!='selection' && group.name !== 'target'">{{group.label}}</span>
                <span v-else-if="group.type!='index' && group.type!='selection' && tableData && tableData.length > 0 && iShowTable" >{{group.label}}</span>
              </th>
              <th class="wy-table-tail"><br></th>
            </tr>
            <tr ref="wySubheaderMain" class="wy-sub-header">
              <th v-for="(column, index) in allColumns" :key="index" 
                  :class="generateColumnHeaderClass(column)"
                  v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px !important', 'text-align': column.align}" @click="handleColumnSort(column)" 
                  @mouseenter="toggleSortCaretHovered(column.sortBy)" @mouseleave="toggleSortCaretHovered(column.sortBy)">
                <el-tooltip :disabled="!isHeaderTooltipVisible || column.label.length < maxSectionNameChars"  placement="top-start" :content="column.label">
                  <span v-if="column.type!='index'" @mouseover="mouseover($event, '% change from last period', '', column, true)" @mouseout="mouseout">{{ concatenateString(column.label) }}</span>
                </el-tooltip>
              </th>
              <th class="wy-table-tail"><br></th>
            </tr>        
          </thead>
          <tbody  ref="wyTableBodyMain" class="wy-body" v-bind:class="{'isTableEditing': isTableEditing}" @mousewheel="handleMousewheelOnMainTable" @scroll="handleScrollingOnMainTable">
            <tr v-for="(row, index) in tableData" :id="'wyTableBodyMain'+index" :key="index" @click="handleClick($event, index, row)" @mouseover="handleMouseover" @mouseleave="handleMouseleave" :class="{'highlightedRow': index == highlightedIndex, 'clickable': isClickable, 'disabled-row':row.blackListed}">
              <td v-for="(column, columnIndex) in allColumns" :key="columnIndex" :col="column.name" v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px', 'text-align': column.align}" 
                  :class="generateColumnClass(column)">
                <!-- empty column (if it's part of the fixed columns) since it will be rendered in fixed column -->
                <!-- <div v-if="column.customCell">
                  <component :is="column.customCell" v-bind="{controller: controller, column: column, row: row, index: index}"></component>
                </div> -->
                <div v-if="column.fixed"></div>
                <!-- index column -->
                <div v-else-if="column.type=='index'"></div>
                <!-- icon column -->
                <div v-else-if="column.type=='icon'"></div>
                <!-- html column -->
                <div v-else-if="column.type=='html'">
                  <div v-if="row[column.name]" v-html="row[column.name]"></div>
                  <div v-else class="el-no-data">N/A</div>
                </div>
                <!-- trend column -->
                <div v-else-if="column.type=='trend'">
                  <div v-html="row[column.name]"></div>
                </div>
                <div v-else-if="column.name=='actionItem'">
                  <div v-if="row[column.name]==null || row[column.name]==false"> - </div>
                  <div v-else class="is-action-item" :class="{'red': checkGreaterThanThreshold(row), 'yellow': !checkGreaterThanThreshold(row)}"></div>   
                </div>
                <!-- no data column -->
                <div v-else-if="row[column.name]==null || row[column.name]=='NaN' || row[column.name]=='+Infinity' || row[column.name]==-1">
                  <!-- <div v-if="row.blackListed" class="el-no-data" @mouseover="handleTooltipOver($event, 'Data not available for blacklisted intents')" @mouseout="handleTooltipOut($event)">N/A</div> -->
                  <div v-if="row.blackListed" class="el-no-data" @mouseover="mouseover($event, 'Data not available for blacklisted intents', 'normal', column)" @mouseout="mouseout">N/A</div>
                  
                  <div v-else-if="column.type=='percentChange'" class="el-no-data" @mouseover="mouseover($event, noDataTooltipForPercentChange, 'normal', column)" @mouseout="mouseout">N/A</div>
                  <div v-else-if="column.type=='target'" class="el-no-data" @mouseover="mouseover($event, noDataTooltipForTarget, 'normal', column)" @mouseout="mouseout">N/A</div>
                  <div v-else class="el-no-data" @mouseover="mouseover($event, (column.noDataTooltip ? column.noDataTooltip : noDataTooltipContent), 'normal', column)" @mouseout="mouseout">N/A</div>
                </div>
                <!-- % change column -->
                <div v-else-if="column.type=='percentChange'" @mouseover="mouseover($event, row[column.tooltipMessage], 'normal', column)" @mouseout="mouseout"  :class="{'arrow-up positive-change-style':row[column.name]>0,'arrow-down negative-change-style':row[column.name]<0,'zero-change-style':row[column.name]==0, 'no-data-tooltip-style': row[column.name] == undefined || row[column.name] == null}">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-if="row[column.name]==0" class="zero-change">0.00</span>
                  <span v-else-if="row[column.name] == undefined || row[column.name] == null" class="zero-change">N/A</span>
                  <span v-else v-html="cellFormatter(Math.abs(row[column.name]))" :class="{'positive-change':row[column.name]>0,'negative-change':row[column.name]<0 }"></span>

                  <span v-if="column.suffix" class="change-suffix">{{column.suffix}}</span>                
                </div>
    
                <!-- tooltip column -->
                <div v-else-if="column.tooltipMessage && row[column.tooltipMessage]" @mouseover="handleTooltipOver($event, row[column.tooltipMessage])" @mouseout="handleTooltipOut($event)">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="cellFormatter(row[column.name])"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
                <!-- bes & bas target column -->
                <!-- <div :class="(parseInt(row.basPercent) >= row[column.name].lessThan && parseInt(row.basPercent) <= row[column.name].greaterThan? 'target-yellow-style': parseInt(row.basPercent) < row[column.name].lessThan? 'target-red-style': parseInt(row.basPercent) > row[column.name].greaterThan? 'target-green-style': 'target-no-color')" v-else-if="column.name == 'basTarget'">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="(parseInt(row.basPercent) >= row[column.name].lessThan && parseInt(row.basPercent) <= row[column.name].greaterThan? cellFormatter(row[column.name].greaterThan): parseInt(row.basPercent) < row[column.name].lessThan? cellFormatter(row[column.name].lessThan): parseInt(row.basPercent) > row[column.name].greaterThan? cellFormatter(row[column.name].greaterThan): cellFormatter(row[column.name].greaterThan))"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
                <div :class="(parseInt(row.besPercent) >= row[column.name].lessThan && parseInt(row.besPercent) <= row[column.name].greaterThan? 'target-yellow-style': parseInt(row.besPercent) < row[column.name].lessThan? 'target-red-style': parseInt(row.besPercent) > row[column.name].greaterThan? 'target-green-style': 'target-no-color')" v-else-if="column.name == 'besTarget'">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="(parseInt(row.besPercent) >= row[column.name].lessThan && parseInt(row.besPercent) <= row[column.name].greaterThan? cellFormatter(row[column.name].greaterThan): parseInt(row.besPercent) < row[column.name].lessThan? cellFormatter(row[column.name].lessThan): parseInt(row.besPercent) > row[column.name].greaterThan? cellFormatter(row[column.name].greaterThan): cellFormatter(row[column.name].greaterThan))"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div> -->
                <div v-else-if="column.name == 'besTarget' && row[column.name] && row[column.name].maxValue==null"> 
                  <span class="el-no-data">N/A</span>
                </div>
                <div v-else-if="column.name == 'besTarget'">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-else v-html="row[column.name] && row[column.name].maxValue"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
                <div v-else-if="column.name == 'basTarget' && row[column.name] && row[column.name].maxValue==null"> 
                  <span class="el-no-data">N/A</span>
                </div>
                <div v-else-if="column.name == 'basTarget'">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="row[column.name] && row[column.name].maxValue"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
                <!-- bes & bas column -->
                <!-- <div v-else-if="row.besPercent == 'N/A' || row.basPercent == 'N/A'">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="cellFormatter(row[column.name])"></span>
                </div> -->
                <div v-else-if="column.name=='besPercent'" :class="(row['besTarget'] && row['besTarget'].maxValue==null?'no-target':parseInt(row.besPercent)>=(row['besTarget'] && row['besTarget'].maxValue)?'target-green-style':'target-red-style')">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="cellFormatter(row[column.name])"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
                <div v-else-if="column.name=='basPercent'" :class="(row['basTarget'] && row['basTarget'].maxValue==null?'no-target':parseInt(row.basPercent)>=(row['basTarget'] && row['basTarget'].maxValue)?'target-green-style':'target-red-style')">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="cellFormatter(row[column.name])"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
                <!-- normal column -->
                <div v-else>
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="cellFormatter(row[column.name])"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
              </td>
              <td class="wy-table-tail"><br></td>
            </tr>
          </tbody>
        </table>

        <!-- Fixed not movable columns overlay table -->
        <table border="0" class="wy-table-fixed">
          <thead :class="(hasShadow ? 'show-fixed-table-shadow':'regular-fixed-thead-style')">
            <tr v-if="!noMainColumn" class="wy-header">
              <th class="fixed" v-for="(group, index) in groupFixedColumns" :key="index" :colspan="group.headerSpan" 
                  v-bind:style="{'min-width': (group.mainColumn?variableColWidth:group.headerWith)+'px !important'}" :class="{'no-right-border':isNameColumnOnly&&index==1  }">
                <span v-if="group.type!='index' && group.type!='selection' && group.name !== 'target'">{{group.label}}</span>
                <span v-else-if="group.type!='index' && group.type!='selection' && tableData && tableData.length > 0 && iShowTable" >{{group.label}}</span>
                <!-- <span v-if="group.mainColumn&&!group.withTotalNumber&&group.name===''" class="total-topic-no-group"></span> -->
                <span v-if="group.mainColumn&&!group.withTotalNumber" class="total-topic-num">Total: {{totalTopicNumber.toLocaleString('en-US')}}</span>
                <span v-if="group.mainColumn&&group.withTotalNumber" class="total-topic-num with-tooltips" @mouseover="mouseover($event, toolTipsTotalNumber, 'normal', 'totalTopic')" @mouseout="mouseout">Total: {{totalTopicNumber.toLocaleString('en-US')}}</span>
              </th>
            </tr>
            <tr class="wy-sub-header">
              <th v-for="(column, index) in fixedColumns" :key="index" v-bind:class="fixedTableHeaderClasses(column, index)"
                  v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px !important'}"  @click="handleColumnSort(column)" 
                  @mouseenter="toggleSortCaretHovered(column.sortBy)" @mouseleave="toggleSortCaretHovered(column.sortBy)">
                <el-checkbox v-if="column.type=='selection'" ref="headerCheckbox" v-model="selectAll" :disabled="tableDataEditable.length===0" @change.native="handleSelectAll" @click.native="handleSelectStopPropagation" :indeterminate="selectedRowsNum > 0 && selectedRowsNum < tableModel.results.length" :checked="selectedRowsNum == tableModel.results.length"/> 
                <span v-else-if="column.type!='index'">{{column.label}}</span>
              </th>
            </tr>  
          </thead>
          <tbody ref="wyTableBodyFixed" class="wy-body" v-bind:class="{'isTableEditing': isTableEditing, 'show-fixed-table-shadow': hasShadow}" @mousewheel="handleMousewheelOnFixedTable" @scroll="handleScrollingOnFixedTable">
            <tr v-for="(row, index) in tableData" :id="'wyTableBodyFixed'+index" :key="index" @click="handleClick($event, index, row)" @mouseover="handleMouseover" @mouseleave="handleMouseleave" :class="{'clickable': isClickable, 'disabled-row':row.blackListed}">
              <td class="fixed" v-for="(column, columnIndex) in fixedColumns" :key="columnIndex" :col="column.name" :class="{'last-child': column.lastChild}" :data-testid="column.dataTestId?column.dataTestId:undefined"
                v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px', 'max-width': (column.mainColumn?variableColWidth:column.minWidth)+'px','text-align': column.align}">

                <!-- <div v-if="column.customCell">
                  <component :is="column.customCell" v-bind="{controller: controller, column: column, row: row, index: index}"></component>
                </div> -->

                <!-- index column -->
                <div v-if="column.type=='index'">
                  {{index+1}}
                </div>
                <!-- selection column -->
                <div v-else-if="column.type=='selection'" class="wy-table-selection">
                  <el-checkbox v-model="row.isSelected" @change.native="handleSelect" @click.native="handleSelectStopPropagation" :disabled="row.isEditable===false"/>
                </div>
                <!-- star column -->
                <div v-else-if="column.type=='star'" class="wy-table-selection">
                  <component :is="column.customCell" v-bind="{parent: module, controller: controller, column: column, row: row, index: index}"></component>
                </div>
                <!-- icon column -->
                <div v-else-if="column.type=='icon'">
                  <div v-if="row[column.name].icon != ''" class="wy-table-icon" v-bind:style="{'background-image': row[column.name].icon}" 
                    @mouseover="handlePopoverOver($event, row[column.name].toolTips)" @mouseout="handlePopoverOut($event)">
                    <div v-if="row[column.name] && row[column.name].value>=1000" class="icon-number small-font">....</div>
                    <div v-else class="icon-number" v-bind:class="{'small-font': (row[column.name] && row[column.name].value>=100 && row[column.name].value<1000)}">{{row[column.name].value}}</div>
                  </div>
                </div>
                <!-- editable column -->
                <div v-else-if="column.type=='editable'" @mouseover="mouseover($event, row[column.name], row.intent? 'left':'right')" @mouseout="mouseout">
                  <div class="wy-table-editor" v-bind:class="{'isEditing': row.isEditing}">
                    <div v-if="row.isNew" class="new-topic">NEW</div>
                    <el-input v-if="row.isEditing&&column.maxCharLength" class="wy-table-content edit limited-length" type="text" v-model="editingValue" :maxlength="column.maxCharLength" show-word-limit @blur="handleEditingBlur($event, column, row)" @keydown="handleKeyDown" @keypress.enter.native="handleEditingConfirm($event, column, row)"></el-input>
                    <el-input v-else-if="row.isEditing" class="wy-table-content edit" type="text" v-model="editingValue"  @blur="handleEditingBlur($event, column, row)" @keydown="handleKeyDown" @keypress.enter.native="handleEditingConfirm($event, column, row)"></el-input>
                    <span v-else class="wy-table-content" v-html="row[column.name]"></span>
                    <span v-if="isRoleAllow(editPermission) && (row.isEditable===undefined || row.isEditable===true) && !column.disableEditFeature" class="el-edit-icon el-edit-pencil" @click="handleEditing($event, column, row, true)" @mouseover="handleTooltipOver($event, 'Edit Topic Name')" @mouseout="handleTooltipOut($event)"></span>
                    <span v-if="isRoleAllow(editPermission) && (row.isEditable===undefined || row.isEditable===true) && !column.disableEditFeature" class="el-edit-icon el-edit-cancel" @click="handleEditing($event, column, row, false)">Cancel</span>
                    <!-- <div v-if="row.isEditable===true&&column.hasInfo&&row[column.name]!=='Others'" class="topic-info" @mouseover="handleTooltipOver($event, 'Associated tasks')" @mouseout="handleTooltipOut($event)" @click="handleInfoClick"></div> -->
                    <!-- <div v-if="row.isEditable===true&&column.hasInfo&&row[column.name]!=='Others'" class="topic-info" @mouseover="mouseover($event, 'Associated tasks', 'normal', '', false)" @mouseout="mouseout" @click="handleInfoClick"></div> -->
                    <wy-info-popover v-if="row.isEditable===true&&column.hasInfo&&row[column.name]!=='Others'" ref="infoPopover" :row="row" :controller="module"></wy-info-popover>
                  </div>
                </div>
                <!-- has tooltips column -->
                <div v-else-if="column.toolTips" @mouseover="mouseover($event, row[column.name], 'normal')" @mouseout="mouseout">
                  <span class="row-overflow-style" v-if="column.label=='Name'" v-html="row[column.name]"></span>
                  <span v-else>
                    <span class="wy-table-content" v-html="row[column.name]"></span>
                  </span>
                </div>
                <!-- normal column -->
                <div v-else @mouseover="mouseover($event, row[column.name], 'normal')" @mouseout="mouseout">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                    <span class="row-overflow-style" v-if="column.label=='Name'" v-html="row[column.name]"></span>
                    <span v-else>
                    {{row[column.name]}}
                    </span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <no-data-page v-else-if="finishParentRender" :search-or-filter="searchOrFilter"> </no-data-page>
    </div>

    <el-tooltip
      ref="tableTooltip"
      placement="bottom"
      effect="dark"
      popper-class="table-tooltip"
      >
      <div slot="content"><span v-html="tableTooltipContent"></span></div>
    </el-tooltip>

    
    <el-popover
      ref="tablePopover"
      placement="bottom"
      title=""
      trigger="hover"      
      popper-class="wy-table-change-popover"
      :content="tablePopoverContent">
    </el-popover>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import WyInfoPopover from './wy-associated-info.vue';
import noDataPage from '../../views/PageNoDataWithAvailableDateRange'

export default {
  name: 'wy-table-list',
  props: ['controller', 'noMainColumn', 'enableSelection', 'rowClickEvent', 'paginationSorting', 'defaultSort', 'searchFieldName', 'searchOrFilter', 'editPermission', 'isHeaderTooltipVisible'],
  components: {
    'no-data-page': noDataPage,
    'wy-info-popover': WyInfoPopover
  },
  data() {
    return {
      module: this,
      iShowTable: false,
      groupColumns: null,
      groupFixedColumns: null,
      sortedColumnName: null,
      sortedType: 1,
      noDataTooltipContent: 'Data not available for this time period',
      noDataTooltipForPercentChange: 'Correct comparison not available for this time period',
      noDataTooltipForTarget: 'Target not available for this time period',
      sortCaretHovered: 0,
      tablePopoverContent: null,
      tableTooltipContent: null,
      isTableEditing: false,
      editingValue: null,
      highlightedIndex: null,
      scrollLeft: 0,
      selectedRowsNum: 0,
      allColsWidth: -1,
      variableColWidth: 291,
      finishParentRender: false,
      selectAll: false,
      toolTipsTotalNumber: '',
      hasShadow: false,
      maxSectionNameChars: 18,
      currentPopover: null
    }
  },


  computed: {
    ...mapGetters(['dataModel', 'tableModel', 'isRoleAllow', 'companyConfig']),

    isClickable() {
      return (this.rowClickEvent!=null)
    },

    fixedColumns() {
      return this.allColumns.filter((item) => item.fixed)
    },

    isNameColumnOnly() {
      return this.allColumns.length <= 2;
    },

    allColumns() {
      let list = [];
      this.groupColumns.forEach(group => {
        let currGroup = group.columns.filter(el => el.selected);
        for (let i = 0; i < currGroup.length-1; i ++)
          currGroup[i].lastChild = false;
        if (currGroup.length)
          currGroup[currGroup.length-1].lastChild = true;
        list = list.concat(currGroup);
      })
      return list;
    },

    tableData() {
      let list = null;
      if(this.searchFieldName && this.dataModel.keyword && this.dataModel.keyword!='') {
        let keywordList = this.dataModel.keyword.split(',').map(item => {
          return item.trim().replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
        });
        list = this.tableModel.results.filter(item => {
          if(item[this.searchFieldName]) {
            return new RegExp(keywordList.join("|"), 'i').test(item[this.searchFieldName].replace("$", "\\$"))
          }
          else {
            return false
          }
        })
      }
      else {
        list = this.tableModel.results;
      }

      if(this.sortedColumnName) {
        if(this.sortedType == 0) {
          list.sort((a,b) => {
            if (typeof a[this.sortedColumnName] === 'string' && typeof b[this.sortedColumnName] === 'string') {
              return b.isNew - a.isNew || b.isEditable - a.isEditable || a[this.sortedColumnName].toLowerCase().localeCompare(b[this.sortedColumnName].toLowerCase());
            } else {
              return b.isNew - a.isNew || b.isEditable - a.isEditable || a[this.sortedColumnName] - b[this.sortedColumnName];
            }
          })
        }
        else {
          if(list.totalResponses){
            list.results.sort((a,b) => {
              if (typeof a[this.sortedColumnName] === 'string' && typeof b[this.sortedColumnName] === 'string') {
                return b.isNew - a.isNew || b.isEditable - a.isEditable || b[this.sortedColumnName].toLowerCase().localeCompare(a[this.sortedColumnName].toLowerCase());
              } else {
                return b.isNew - a.isNew || b.isEditable - a.isEditable || b[this.sortedColumnName] - a[this.sortedColumnName];
              }
            })
          } 
          else {
            list.sort((a,b) => {
              if (typeof a[this.sortedColumnName] === 'string' && typeof b[this.sortedColumnName] === 'string') {
                return b.isNew - a.isNew || b.isEditable - a.isEditable || b[this.sortedColumnName].toLowerCase().localeCompare(a[this.sortedColumnName].toLowerCase());
              } else {
                return b.isNew - a.isNew || b.isEditable - a.isEditable || b[this.sortedColumnName] - a[this.sortedColumnName];
              }
            })
          }
          
        }
      }
      if(list.totalResponses){
        return list.results;
      } else {
        return list;
      }
    },

    tableDataEditable() {
      return this.tableData.filter(item => (item.isEditable===undefined || item.isEditable));
    },

    totalTopicNumber() {
      return WyUtils.formatterNumber(this.tableData.length);
    },
  },

  watch: {
    'dataModel.keyword': function() {
      this.clearSelection();
    }
  },

  mounted: function() {
    this.resizeObserver = new ResizeObserver(this.handleWindowResize).observe(this.$refs.wyTableList);
    // this.finishParentRender = true;
  },

  updated: function() {
    this.handleWindowResize();
  },

  methods: {
    ...mapMutations(['updateTableModelSelectedRows']),

    concatenateString(label) {
      return WyUtils.maxString(label, this.maxSectionNameChars, '...')
    },

    cellFormatter(value) {
      if(typeof value == "number") {
        value = WyUtils.formatterNumber(value);
      }
      return value;
    },

    formatNumberWithTwoZero(val) {
      if(val == 0) {
        return '00.00';
      }
      else {
        return val;
      }
    },

    fixedTableHeaderClasses(column, index) {
      let classes = {'lastChild': column.lastChild, 'sortable': column.sortable, 'fixed': true, 'no-right-border': this.isNameColumnOnly&&index==1, 'sorted': this.sortedColumnName==column.sortBy, 
          'ascending': this.sortedType^this.sortCaretHovered==0, 'descending': this.sortedType^this.sortCaretHovered==1};
      classes[column.name] = true;
      return classes;
    },
    generateColumnClass(column) {
      let classes = {'last-child': column.lastChild, 'align-right': column.align == 'right'};
      classes[column.name] = true;
      return classes;
    },

    generateColumnHeaderClass(column) {
      let classes = {'lastChild': column.lastChild, 'sortable': this.sortableColumn(column), 'sortDisabled': this.isColumnSortable(column), 'sorted': this.sortedColumnName==column.sortBy,'ascending': this.sortedType^this.sortCaretHovered==0, 'descending': this.sortedType^this.sortCaretHovered==1};
      classes[column.name] = true;
      return classes;
    },

    checkGreaterThanThreshold(row) {
      if(this.companyConfig && this.companyConfig.appPages && this.companyConfig.appPages.nluIntentPerformancePage && this.companyConfig.appPages.nluIntentPerformancePage["thresholdOfTotalIssues"]) {
        let threshold = this.companyConfig.appPages.nluIntentPerformancePage["thresholdOfTotalIssues"]
        if(row.issuesPercent && row.issuesPercent > threshold) {
          return true
        }
        else {
          return false
        }
      }
      else {
        return false
      }
    },

    handleColumnSort({ sortBy, sortable, sortType }) {
      if(this.paginationSorting) {
        if (sortType != null) {
          this.sortedColumnName = sortBy;
          this.sortedType = sortType;
          return;
        }
      
        if (this.sortedColumnName != sortBy) {
          this.sortedType = 1;
        } else {
          this.sortedType = this.sortedType == 0 ? 1 : 0;
        }
      
        if (this.sortedColumnName != sortBy) {
          this.sortedColumnName = sortBy;
          this.sortCaretHovered = 1;
        }
        sortType = this.sortedType;
        this.paginationSorting({ sortBy, sortable, sortType })
      }
      else {
        this.handleSort({ sortBy, sortable, sortType })
      }
    },

    handleSort({ sortBy, sortable, sortType }) {
      if (sortable && !this.isColumnSortable({ sortBy, sortable, sortType })) {
        if (sortType != null) {
          this.sortedColumnName = sortBy;
          this.sortedType = sortType;
          return;
        }
      
        if (this.sortedColumnName != sortBy) {
          this.sortedType = 1;
        } else {
          this.sortedType = this.sortedType == 0 ? 1 : 0;
        }
      
        if (this.sortedColumnName != sortBy) {
          this.sortedColumnName = sortBy;
          this.sortCaretHovered = 1;
        }
      
        // Sort the tableData array
        /*this.tableData.sort((a, b) => {
          const aIsNull = a[sortBy] == null;
          const bIsNull = b[sortBy] == null;
        
          if (this.sortedType === 1) {
            // Sorting in ascending order
            if (aIsNull && !bIsNull) {
              return 1; 
            } else if (!aIsNull && bIsNull) {
              return -1;
            }
          } else {
            // Sorting in descending order
            if (aIsNull && !bIsNull) {
              return -1; 
            } else if (!aIsNull && bIsNull) {
              return 1; 
            }
          }
        
          // Both a and b are either "N/A" or non-"N/A", perform regular sorting
          if (a[sortBy] < b[sortBy]) {
            return -1 * this.sortedType;
          } else if (a[sortBy] > b[sortBy]) {
            return 1 * this.sortedType;
          }
          return 0;
        });*/
      }
    },

    handleSelect(e) {
      e.preventDefault();
      e.stopPropagation();
      let selectedRows = this.tableData.filter(item => item.isSelected);
      this.updateTableModelSelectedRows(selectedRows);
      this.selectedRowsNum = selectedRows.length;

      if(this.tableData.length == this.selectedRowsNum) {
        this.selectAll = true;
      }

      if(this.selectedRowsNum == 0) {
        this.selectAll = false;
      }
      this.$root.$emit(`select-all-change-events`, this.selectedRowsNum != 0 || this.selectAll? true: false)
    },

    handleSelectStopPropagation(e) {
      e.stopPropagation();
    },

    handleSelectAll(e) {
      e.preventDefault();
      e.stopPropagation();
      let selectedRows = this.tableData.filter(item => {item.isSelected=(e.target.checked && (item.isEditable===undefined || item.isEditable===true)); return item.isSelected});
      this.updateTableModelSelectedRows(selectedRows);
      this.selectedRowsNum = selectedRows.length;
      this.$root.$emit(`select-all-change-events`, this.selectedRowsNum != 0 || this.selectAll? true: false)
    },

    clearSelection() {
      let selectedRows = this.tableData.filter(item => {item.isSelected=false; return false});
      this.updateTableModelSelectedRows(selectedRows);
      this.selectedRowsNum = 0;
      this.$root.$emit(`select-all-change-events`, false)
      this.selectAll = false;
    },

    handlePopoverOver(e, content) {
      this.$refs.tablePopover.doClose();
      this.tablePopoverContent = content;
      this.$refs.tablePopover.referenceElm = e.target;
      setTimeout(() => {
        this.$refs.tablePopover.doShow();
      }, 100)
    },

    handlePopoverOut() {
      this.tablePopoverContent = null;
      this.$refs.tablePopover.referenceElm = null;
      setTimeout(() => {
        this.$refs.tablePopover.doClose();
      }, 10)
    },

    handleTooltipOver(e, content) {
      if(this.$refs.tableTooltip){
        this.$refs.tableTooltip.hide();
        this.$refs.tableTooltip.handleClosePopper();
        this.$refs.tableTooltip.referenceElm = e.target;
        
        this.$refs.tableTooltip.$refs.popper.style.background = "#0C2837";
        this.$refs.tableTooltip.$refs.popper.style.fontSize = "14.16px";
      }
      this.tableTooltipContent = content;
      setTimeout(() => {

        if(this.$refs.tableTooltip){
          this.$refs.tableTooltip.show();
          this.$refs.tableTooltip.handleShowPopper();

          setTimeout(()=>{
            if (this.$refs.tableTooltip.$refs.popper.getBoundingClientRect().x < 0 || Math.abs(e.y - this.$refs.tableTooltip.$refs.popper.getBoundingClientRect().y) > 33) {
              this.$refs.tableTooltip.hide();
              this.$refs.tableTooltip.handleClosePopper();
            }
          }, 0)
        }
      }, 0)
    },

    handleTooltipOut() {
      if(this.$refs.tableTooltip){
        this.$refs.tableTooltip.hide();
        this.$refs.tableTooltip.handleClosePopper();
      }
    },

    handleMousewheelOnMainTable(e) {
      e.preventDefault();
      e.stopPropagation();
      this.$refs.wyTableBodyFixed.scrollTop += e.deltaY;  // y scroll fixed overlay table
      this.$refs.wyTableBodyMain.scrollTop += e.deltaY;  // y scroll main table
      this.$refs.wyTableBodyMain.scrollLeft += e.deltaX;  // x scroll main table
      this.scrollLeft = Math.max(0, this.$refs.wyTableBodyMain.scrollLeft) * -1;  // x scroll main header
    },

    handleMousewheelOnFixedTable(e) {
      e.preventDefault();
      e.stopPropagation();
      this.$refs.wyTableBodyFixed.scrollTop += e.deltaY;  // y scroll fixed overlay table
      this.$refs.wyTableBodyMain.scrollTop += e.deltaY;  // y scroll main table
    },

    handleScrollingOnMainTable(e) {
      // x scroll fixed overlay table with main table
      this.scrollLeft = e.target.scrollLeft * -1;
      if(this.$refs.wyHeaderMain) {
        this.$refs.wyHeaderMain.style.left = this.scrollLeft + 'px';
      }
      if(this.$refs.wySubheaderMain) {
        this.$refs.wySubheaderMain.style.left = this.scrollLeft + 'px';
      } 
      if(e.target.scrollLeft * -1 < -1) {
        this.hasShadow = true
      }
      else {
        this.hasShadow = false
      }
      // y scroll fixed overlay table with main table
      this.$refs.wyTableBodyFixed.scrollTop = e.target.scrollTop;
      this.closeAllPopovers();
      // setTimeout(()=>{
      //   document.querySelectorAll(".el-tooltip__popper.is-dark").forEach(el=>{
      //     el.style.display = 'none';
      //   })
      // }, 150)
    },

    handleScrollingOnFixedTable(e) {
      // Handles the case where the fixed table is scrolled when user searchers keyword using Ctrl + F
      this.$refs.wyTableBodyMain.scrollTop = e.target.scrollTop;
    },

    closeAllPopovers() {
      if(this.currentPopover && this.currentPopover.visible) {
        this.currentPopover.closeAllPopovers();
        
      }
    },

    handleClick(e, rowIndex, row) {
      let target = e.target;
      if((target.hasAttribute('class') && target.getAttribute('class') === 'wy-table-selection') || (target.hasAttribute('col') && target.getAttribute('col') === 'index')) {
        return; // do not handle click on index column
      }
      if(this.rowClickEvent && !this.isTableEditing && !target.classList.contains("wy-table-select") && !target.parentNode.classList.contains("wy-table-select")&& !target.classList.contains("el-button")) {
        this.rowClickEvent(rowIndex, row);
      }
    },

    handleEditing(e, column, row, isEditing) {
      e.preventDefault();
      e.stopPropagation();
      row.isEditing = isEditing;
      this.isTableEditing = isEditing;
      this.editingValue = isEditing ? row[column.name].replace(/<span class='highlight-keywords'>|<\/span>/g, "") : null;
      
      setTimeout(() => {
        let inputEl = e.target.parentNode.querySelector("input.wy-table-content");
        if (inputEl && inputEl.setSelectionRange) {
            inputEl.setSelectionRange(0, 0);
            inputEl.focus();
        }
      }, 100)
    },

    handleEditingConfirm(e, column, row) {
      e.preventDefault();
      e.stopPropagation();
      if(this.editingValue) {
        row.isEditing = false;
        this.isTableEditing = false;
        if(row[column.name] != this.editingValue.trim()) {
          this.controller.updateTableData(row, column.name, this.editingValue.trim());
        }        
      }
    },

    handleEditingBlur(e, column, row) {
      e.preventDefault();
      e.stopPropagation();
      setTimeout(() => {
        if(row.isEditing && this.editingValue) {
          this.handleEditingConfirm(e, column, row);
        }
      }, 200)
    },

    handleKeyDown(event) {
      if(event.keyCode === 13) {
        event.target.blur();
      }
    },

    handleMouseover(e) {
      // this.highlightedIndex = e.target.closest('tr').sectionRowIndex;
      document.querySelector("#wyTableBodyMain" + e.target.closest('tr').sectionRowIndex).classList.add("highlightedRow")
      document.querySelector("#wyTableBodyFixed" + e.target.closest('tr').sectionRowIndex).classList.add("highlightedRow")
    },

    handleMouseleave(e) {
      // this.highlightedIndex = null;
      document.querySelector("#wyTableBodyMain" + e.target.closest('tr').sectionRowIndex).classList.remove("highlightedRow")
      document.querySelector("#wyTableBodyFixed" + e.target.closest('tr').sectionRowIndex).classList.remove("highlightedRow")
    },

    toggleSortCaretHovered(columnName) {
      if (columnName == this.sortedColumnName)
        this.sortCaretHovered = 1 - this.sortCaretHovered;
    },

    refreshTable(options) {
      // preapare selected columns
      this.handleColumnSort({sortBy: this.defaultSort.name, sortType: this.defaultSort.type, sortable: true});
      
      let groupMap = {};
      let cloneColumns = JSON.parse(JSON.stringify(this.dataModel.columns));
      cloneColumns.forEach((column) => {
        column.sortBy = column.sortBy || column.name;

        if(column.groupHidden) {
          if(column.selected) {
            groupMap[column.group] = column;
          }
        }
        else {
          if(!groupMap[column.group]) {
            groupMap[column.group] = {
              label: column.group,
              name: column.group.replace(' ', '-').toLowerCase(),
              columns: [],
              type: column.type,
              isIndeterminate: false,
              fixed: column.fixed,
              mainColumn: column.mainColumn,
              withTotalNumber: column.withTotalNumber,
              width: column.minWidth,
              checked: false,
              customClass: column.customClass ? column.customClass : ''
            };
          }
          if(column.selected) {
            groupMap[column.group].columns.push(column);
            // // add children
            // if(!column.parent) {
            //   this.dataModel.columns.filter(el => el.parent==column.name).forEach(childColumn => {
            //     groupMap[column.group].columns.push(childColumn);
            //   });
            // }
          }
        }        
      });

      let groupList = [];
      let groupFixedList = [];
      Object.keys(groupMap).forEach(key => {
        let group = groupMap[key];
        if(group.columns == undefined || group.columns.length > 0) {
          // prepare selected colum list
          groupList.push(group);
          // calculate the header width and column spans
          if(group.columns.length > 1) {
            group.headerWith = group.columns.reduce((total, item, index) => {
              total+=item.minWidth+(index>0?20:0); 
              return total;
            }, 0);
          }
          else {
            //If there is only 1 field in this group, group header and field header min-width = 180px
            group.headerWith = ((group.headerWith > 180) || (100 > group.headerWith)) ? group.headerWith : 180; 
            group.columns.reduce((total,item) => {
              item.minWidth = (item.minWidth < 100 || item.minWidth > 180) ? item.minWidth : 180;
              group.headerWith = item.minWidth;
            }, 0);
          }
          group.headerSpan = group.columns.filter(el => el.selected).length;
          
          // prepare fixed column list
          let fixedColumns = group.columns.filter((item) => item.fixed);
          if(fixedColumns.length > 0) {
            groupFixedList.push(group)
          }
        }
      });

      this.allColsWidth = 0;
      for (let i = 0; i < groupList.length; i ++) {
        if (!groupList[i].mainColumn)
          this.allColsWidth += groupList[i].headerWith + 20;
      }
      // this.variableColWidth will be updated in updated() method
      this.controller.loading = true;
      if(!options || !options.disablePageLoading) {
        this.controller.$router.app.setLoading(true);
      }
      this.iShowTable = false;
      setTimeout(() => {
        this.controller.loading = false;
        if(!options || !options.disablePageLoading) {
          this.controller.$router.app.setLoading(false);
        }
        this.groupColumns = groupList;
        this.groupFixedColumns = groupFixedList;
        this.iShowTable = true; 
        this.scrollLeft = 0;
        this.finishParentRender = true;
      }, 200);
      if(this.tableModel.totalNumber) {
        this.toolTipsTotalNumber = ' out of ' + this.tableModel.totalNumber.toLocaleString('en-US') + ' topics';
      }
      
      this.clearSelection();
    },

    handleWindowResize() {
      if(this.$refs.wyTableBodyMain) {
        this.$refs.wyTableBodyFixed.style.height = this.$refs.wyTableBodyMain.clientHeight + "px";
        if (this.allColsWidth >= 0) {
          // The next four lines of code are same as this.variableColWidth = Math.max(408, this.$refs.wyTableBodyMain.clientWidth - this.allColsWidth - 100);
          // except it prevents an oscillation phenomenon in Chrome when there are no other columns and we toggle the sidebar
          // This is done by only updating the variableColWidth when it's different from two steps ago
          let tmp = Math.max(291, this.$refs.wyTableBodyMain.clientWidth - this.allColsWidth - 34);
          if (tmp != this.variableColWidthPrev) {
            this.variableColWidthPrev = this.variableColWidth;
            this.variableColWidth = tmp;
          }
        }
      }
    },

    sortableColumn(column) {
      return column.sortable;
    },

    isColumnSortable(column) {
      // For all columns, disable sorting when all value are N/A or 0
      if (column.sortable) {
        const sortByColumn = column.sortBy
        return this.tableData.every(row => {
          return row[sortByColumn] == null || row[sortByColumn] == -1000000 || row[sortByColumn] == 0 || row[sortByColumn] == -1
        })
      }
      else {
        return false
      }
    },

    mouseover(e, tooltips, type, column, isFromHeader) {
      e.stopPropagation();
      let ignoreClassName = e.target.className != 'assigned-intent' && e.target.className != 'el-select-dropdown__item' && e.target.className != 'el-select-dropdown__item hover' 
      && e.target.className != 'ignore-wrap-div' && e.target.className != 'existing-intent-popover-item' && e.target.className != 'el-select__caret el-input__icon el-icon-arrow-up' 
      && e.target.className != 'el-select el-select--mini' && e.target.className != 'change-suffix'

      let isCustomizedTooltip = false;
      if(column && column.label == '% change'|| column == 'totalTopic' || column == '' || (!isFromHeader && column && column.label && column.label == 'Total')) {
        isCustomizedTooltip = true;
      }
      if(((e.target.classList.contains('wy-table-content')|| e.target.classList.contains('row-overflow-style') || e.target.classList.contains('highlight-keywords')) && !e.target.classList.contains('edit')) || isCustomizedTooltip){
        if(tooltips && tooltips.trim() != '' && ignoreClassName) {
          let tooltipsEl = document.querySelector(".site-tooltips");
          if(tooltipsEl) {
            tooltipsEl.classList.add('black-background');
            tooltipsEl.innerHTML = tooltips
            tooltipsEl.style.display = 'block';
            tooltipsEl.style.left = `${e.target.getBoundingClientRect().x + 10}px`;
            if(column && column.label == '% change') {
              tooltipsEl.style.left = `${e.target.getBoundingClientRect().x - 50}px`;
            }
            if(type == "right") {
              tooltipsEl.style.right = "10px";
              tooltipsEl.style.left = "380px";
            }
            if(type == "left") {
              tooltipsEl.style.left = "346px";
              tooltipsEl.style.right = "unset";
            }
            if (type == 'normal') {
              tooltipsEl.style.left = `${e.target.getBoundingClientRect().x - tooltipsEl.getBoundingClientRect().width / 2 + e.target.getBoundingClientRect().width / 2}px`;
            }
            tooltipsEl.style.top = `${e.target.getBoundingClientRect().top + e.target.getBoundingClientRect().height + 5}px`;
          }
        }
        else {
          e.preventDefault();
        }
      }
    },
    mouseout(e) {
      e.stopPropagation();
      let tooltipsEl = document.querySelector(".site-tooltips");
      if(tooltipsEl) {
        tooltipsEl.style.display = 'none';
      }
    }
  }
}
</script>

<style lang="scss">
.el-tooltip__popper.is-dark.table-name-custom-tooltip{
  border-radius: unset;
  border: 1px solid #E6E6E6;
  background-color: #E6E6E6;
  color: var(--site-font-tertiary-color);
  font-size: 14px;
  line-height: 18px;
  left: 345px !important;
  .popper__arrow{
    display: none !important;
  }
}
.el-tooltip__popper.is-dark.percentage-tooltip-style{
  font-size: 14px !important;
  background: #0C2837 !important;
  width: 167px;
  font-family: 'Helvetica Neue', Arial, Helvetica, sans-serif !important;
}
.el-tooltip__popper.is-dark.table-tooltip {
  padding: 6px;
}
.wy-table-list {
  width: 100%;
  span.row-overflow-style {
    max-width: 398px;
    word-break: break-word;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  thead tr.wy-header > th:first-child,thead tr.wy-sub-header > th:first-child {
    padding-left: 19px;
  }
  span.el-tooltip.row-overflow-style {
    justify-content: flex-start;
    width: 100%;
    max-width: 408px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .target-red-style {
    // background-color: #FBE4E3;
    width: 45px;
    font-size: 13px;
    height: 22px;
    // color: #C11149;
    display: flex;
    align-items: center;
    padding-top: 5px;
    padding-left: 18px;
    margin-left: 30%;
    background-size: 25%;
    background-position-y: 8px;
    background-position-x: 9px;
    // border-radius: 15px;
    background-repeat: no-repeat;
    background-image: url('../../assets/icons/icon_no-alerts.svg')!important;
  }
  .basPercent .target-red-style {
    width: 55px;
    background-size: 21%;
    margin-left: 45%;
  }
  .besPercent .no-target {
    margin-left: 0%;
  }
  .basPercent .no-target {
    margin-left: 45%;
  }
  .target-no-color {
    background-color: transparent;
    width: 45px;
    font-size: 13px;
    height: 18px;
    color: var(--site-font-chart-color);
    padding-top: 5px;
    padding-left: 18px;
    margin-left: 30%;
    background-size: 25%;
    margin-right: 10px;
    background-position-y: 6px;
    background-position-x: 9px;
    border-radius: 15px;
    background-repeat: no-repeat;
    //background-image: url('../../../assets/icons/icon_target_red.svg')!important;
  }
  .highlight-keywords {
    background-color: rgba(255, 177, 0, 0.22);
  }
  .target-yellow-style {
    // background-color: #FBFCDB;
    width: 45px;
    font-size: 13px;
    height: 22px;
    // color: #8A5608;
    display: flex;
    align-items: center;
    padding-top: 5px;
    padding-left: 18px;
    margin-left: 30%;
    background-size: 25%;
    background-position-y: 8px;
    background-position-x: 9px;
    // border-radius: 15px;
    background-repeat: no-repeat;
    background-image: url('../../assets/icons/icon_target_yellow.svg')!important;
  }
  .target-green-style {
    // background-color: #D9EEDF;
    width: 45px;
    font-size: 13px;
    height: 22px;
    // color: #186731;
    display: flex;
    align-items: center;
    padding-top: 5px;
    padding-left: 18px;
    margin-left: 30%;
    background-size: 25%;
    background-position-y: 8px;
    background-position-x: 9px;
    // border-radius: 15px;
    background-repeat: no-repeat;
    background-image: url('../../assets/icons/icon_has-alerts.svg')!important;
  }
  .basPercent .target-green-style {
    width: 55px;
    background-size: 21%;
    margin-left: 45%;
  }

  .wy-table-wrapper {
    position: relative;

    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    background-color: white;
  }

  table {    
    border-spacing: 0px;
    border-collapse: separate;

    font-size: 14px;
  }

  thead {
    display: table;
    table-layout:fixed;
    background-color: #F5F8FA;
    padding: 0px;
    tr{
      display: block;
      th {
        text-align: left;
        color: var(--site-font-secondary-color);
        font-size: 14.5px;
        padding: 15px 10px;
        height: 19px;
        min-height: 19px;
      }
    }

    tr.wy-header, tr.wy-sub-header {
      > th:first-child {
        min-width: 34px !important;
      }
    }

    .wy-header {
      position: relative;
      left: 0px;
      font-size: 14.5px;
      font-weight: 500;

      th {
        border-right: 1px solid #CAD2DA;
        font-weight: 500;
        vertical-align: top;
        text-align: center;
      }
    }

    .wy-sub-header {
      position: relative;
      left: 0px;
      th:first-child span {
        position: relative;
        top: -12px;
      }
      th {
        font-size: 13px;
        vertical-align: top;
        text-align: center;
        span {
          position: relative;
          top: 2px;
        }
      }
      th.sortable {
        span {
          position: initial;
        }
      }
      th.lastChild {
        border-right: 1px solid #CAD2DA;
      }
      th.fixed.index label.el-checkbox {
        position: relative;
        bottom: -1px;
      }
      th.sortable::after {  // this reserves space for the up-arrow/down-arrow icon (not shown yet)
        content: "";
        display: inline-block;
        height: 15px;
        width: 15px;
        position: relative;
        top: 4px;
        left: 4px;
        background-repeat: no-repeat;
      }
      th.sortable.sortDisabled::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
      }
      th.sortable.sortDisabled:not(.sorted):hover::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
      }
      th.sortable.sortDisabled::after {
        opacity: 0.4;
      }
      th.sortable.sorted.ascending::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
      }
      th.sortable.sorted.descending::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
      }
      th.sortable.sorted.ascending:hover::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
      }
      th.sortable.sorted.descending:hover::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
      }
      th.sortable:hover {
        background-color: unset;
        cursor: pointer;
        .sort-caret.ascending {
          background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
        }
        .sort-caret.descending {
          background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
        }
      }
      th.sortable:not(.sorted):hover::after {
        background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
      }
      th:not(.sortable) {
        span {
          padding-left: 0;
        }
      }
    }
  }

  tbody.isTableEditing {
    tr {
      cursor: default !important;
      .el-edit-pencil {
        display: none !important;
      }
      .topic-info {
        display: none !important;
      }
    }
  }
  
  tbody {
    display: block;

    tr {
      table-layout: fixed;
      display: block;
      cursor: default;

      td {
        padding: 1px 10px;
        height: 57px;
        border-bottom: 1px solid #eee;
        background-color: transparent;

        div {
          display: flex;
          justify-content: center;
        }
      }
      td.align-right div {
        // display: block;
        // padding-right: 12px;
      }

      .wy-table-select .el-checkbox__original {
        margin: 0;
      }

      td.last-child {
        border-right: 4px solid transparent;
      }

      td.wy-table-tail {
        padding: 0;
      }
    }

    tr.clickable {
      cursor: pointer;
    }

    .highlightedRow td.fixed.last-child {
      background-color: #f5f7fa !important;
    }
  }

  .highlightedRow td {
    background-color: #f5f7fa !important;
  }

  .wy-table-select {
    min-width: 20px;
    position: relative;
  }

  .wy-table-tail {
    min-width: 22px;
    display: none;
  }

  .wy-table-main {
    width: 100%;
    max-width: 100%;
    thead {
      width: fit-content;
      th {
        background-color: #F5F8FA;
      }
      th:last-child, th:nth-last-child(2) {
        border-right: 4px solid transparent;
      }
      tr {
        // float: left;  // THIS PLACE IS WHERE SAFARI MESSED UP WHEN YOU ZOOM OUT THE PAGE (BUT CHROME AND FIREFOX DIDN'T)
        width: calc(100vh + 10000px);
        background: #F5F8FA;
        border-bottom: 1px solid #CAD2DA;
        :first-child {
          padding-left: 18px;  // adding the same amt of padding as main column in fixed table
        }
      }
      .wy-header {
        span {
          padding-left: 5px;
        }
      }
    }
    tbody {      
      height: calc( 100vh - 245px );
      overflow: auto;
      width: 100%;
      max-width: 100%;
      tr {
        // float: left;  // THIS PLACE IS WHERE SAFARI MESSED UP WHEN YOU ZOOM OUT THE PAGE (BUT CHROME AND FIREFOX DIDN'T)
        width: calc( 100vw - 370px );
        td:first-child {
          padding-left: 20px;  // adding the same amt of padding as main column in fixed table
        }
        td .positive-change-style {
          background-color: #F4F4FC;
          border: 1px solid #F4F4FC;
          color: #6446BB;
          padding-left: 24px !important;
          padding-right: 15px !important;
          font-weight: 400;
          border-radius: 30px;
          line-height: 34px;
          text-align: right;
          display: flex;
          font-size: 14px;
          height: 30px;
          width: fit-content;
          align-items: center;
          margin: auto;
          
        }
        td .negative-change-style {
          background-color: #EFFCFF;
          border: 1px solid #EFFCFF;
          color: #3961AE;
          padding-left: 24px !important;
          padding-right: 15px !important;
          border-radius: 30px;
          font-weight: 400;
          line-height: 34px;
          text-align: right;
          display: flex;
          align-items: center;
          font-size: 14px;
          height: 30px;
          width: fit-content;
          margin: auto;
        }
        .arrow-up {
          background: url('../../assets/icons/icon_performance_arrow_up.svg') no-repeat;
          background-position-y: 7.87px;
          background-position-x: 10px;
          background-size: 11.95px;
        }
        .arrow-down {
          background: url('../../assets/icons/icon_performance_arrow_down.svg') no-repeat;
          background-position-y: 7.87px;
          background-position-x: 10px;
          background-size: 11.95px;
        }

        td .no-data-tooltip-style, td .zero-change-style {
          background-color: #E4E8EC;
          border: 1px solid #E4E8EC;
          border-radius: 30px;
          color: var(--site-font-tertiary-color);
          padding-left: 24px !important;
          padding-right: 24px !important;
          align-items: center;
          font-weight: 400;
          line-height: 34px;
          text-align: right;
          display: flex;
          font-size: 14px;
          height: 30px;
          width: fit-content;
          margin: auto;
        }
        td span.positive-change {
          color: #4448A2;
          margin-left: 4px;
        }
        td span.negative-change {
          color: #3E6891;
          margin-left: 4px;
        }
        td span.zero-change {
          color: var(--site-font-tertiary-color);
        }
      }
      // border-right: 9px solid #eee;
      border-bottom: 1px solid #eee;
    }
  }

  .wy-table-fixed {
    position: absolute;
    top: 0px;
    text-align: center;
    // border-left: 1px solid #eee;
    //z-index: 2;
    thead {
      th:nth-last-child(1) {
        padding-left: 20px;
        border-bottom: 1px solid #CAD2DA;
        text-align: left !important;
      }
      .total-topic-num.with-tooltips {
        cursor: pointer;
      }
      .no-right-border {
        border-right: 0px !important;
      }
    }
    tbody {
      height: calc( 100vh - 270px);
      overflow: auto;
      -ms-overflow-style: none;  // hiding scrollbar on IE
      scrollbar-width: none;     // hiding scrollbar on Firefox
                                 // see below for Chrome
      width: fit-content;
      td:nth-last-child(1) {
        padding-left: 21px;
        padding-right: 34px;
        font-weight: 500;
        div {
          justify-content: flex-start;
        }
      }

      .wy-table-selection {
        padding-left: 6px;
      }
    }
    tbody.show-fixed-table-shadow {
      box-shadow: 5px -2px 10px -10px grey;
      background-color: white;
    }
    tbody::-webkit-scrollbar {    // hiding scrollbar on Chrome
      display: none;
    }
    input {
      margin: 3px;
    }
  }

  .el-no-data {
    cursor: pointer;
    padding: 5px 10px;
    border-radius: 30px;
    background-color: #E4E8EC;
    width: 50px;
  }

  tbody tr td.align-right .el-no-data {
    padding-right: 12px !important;
    padding: 5px 0px;
    text-align: right;
    width: 40px;
    margin-left: 31px;
    display: inline-block;
  }

  tbody tr td.align-right.last-child .el-no-data {
    padding: 5px 0px;
    margin-right: 36px;
  }

  tbody tr td.last-child .el-no-data {
    padding: 5px 0px;
    margin-right: 0px;
  }

  .is-action-item.red {
    cursor: pointer;
    background-image: url('../../assets/icons/icon_under_trained_intent_red.svg') !important;
    // background-size: 75%;
    background-repeat: no-repeat;
    background-position-y: 2px;
    background-position-x: 1px;
    background-color: transparent !important;
    height: 20px;
    width: 20px;
    border: 0px;
    margin-left: 0px;
    background-size: 88%;
  }
  
  .is-action-item.yellow {
    cursor: pointer;
    background-image: url('../../assets/icons/icon_under_trained_intent_yellow.svg') !important;
    // background-size: 75%;
    background-repeat: no-repeat;
    background-position-y: 2px;
    background-position-x: 1px;
    background-color: transparent !important;
    height: 20px;
    width: 20px;
    border: 0px;
    margin-left: 0px;
    background-size: 88%;
  }

  .icon-number {
    background-color: #80a6cd;
    border-radius: 10px;
    border: 1px solid #FFF;
    height: 16px;
    width: 16px;
    position: relative;
    top: -10px;
    left: 8px;
    font-size: 9px;
    padding-top: 1px;
    padding-left: 0px;
    line-height: initial;
    color: #FFF;
    text-align: center;
  }

  .small-font {
    font-size: 6px !important;
    padding-top: 3px !important;
  }

  .wy-table-icon {
    display: inline-block;
    width : 25px;
    height: 25px;
    background-repeat: no-repeat;
    background-size: 55%;
    position: relative;
    top: 10px;
  }

  .wy-table-change-popover.el-popover--plain {
      padding: 8px 10px;
      border-radius: 5px;
      background-color: var(--site-font-chart-color);
      color: #FFF;
      font-size: 12px;
    .popper__arrow {
      filter: invert(1);
    }
  }

  .wy-table-alert-popover.el-popover--plain {
    background: #80A6CD;
    color: #FFF;
    border-radius: 10px;
    padding: 5px 10px;
    display: inline-block;
    .popper__arrow {
      display: none;
    }
  }

  .wy-table-editor {
    padding: 0;
    width: 100%;
    display: flex;
    align-items: center;
    .new-topic {
      background-color: #FDF0F2;
      color: #E44C95;
      font-size: 10px;
      margin-right: 10px;
      padding: 3px;
      border-radius: 3px;
    }
    .topic-info {
      display: none;
      // padding-left: 10px;
      padding: 8px;
      border: 0px;
    }
  }

  .wy-table-editor.isEditing {
    pointer-events: none;
    .el-edit-cancel {
      display: inline-block;
      margin-top: 5px;
      font-weight: 500;
      color: #106fbc;
      border-right: 15px solid transparent!important;
      font-family: 'Helvetica Neue', sans-serif;
    }
    .wy-table-content {
      display: inline-block;
      width: 255px;
      padding: 5px 10px;
      margin-right: -10px !important;
      pointer-events: initial;
    }
    .el-input__inner {
      background-color: #FFF;
      border-radius: 4px;
      border: 1px solid #DCDFE6;
      position: relative;
      right: 10px;
      height: 25px;
      &:focus-within{
        border: 1px solid #106FBC;
      }
    }
    .wy-table-content.edit.limited-length.el-input .el-input__suffix {
      right: 19px;
      .el-input__count .el-input__count-inner {
        padding: 5px 0px 0 0px;
        font-size: 9px;
      }
    }
    .el-input__inner {
      padding: 0 28px 0 15px;
    }
    input.el-input__inner {
      border-width: 1px !important;
    }
  }

  .highlightedRow {
    .el-edit-pencil {
      display: block;
    }
    // .topic-info.el-icon-info {
    //   display: block;
    // }
    .topic-info {
      display: block !important;
      height: 20px;
      width: 20px;
      top:1px;
      background: url('../../assets/icons/icon_info.svg') no-repeat;
      background-size: 20px;
      left: 10px;
      position: relative;
      cursor: pointer;
    }
  }

  .wy-table-content {
    display: inline-block;
    border: 1px solid transparent;
    vertical-align: middle;
    margin-right: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .el-edit-icon  {
    display: none;
    cursor: pointer;
    background-size: 16px;
    background-repeat: no-repeat;
    background-position-y: 3px;
    background-position-x: 0px;
    background-color: transparent;
    border-radius: 16px;
    height: 20px;
    width: 20px;
    position: relative;
    left: 5px;
    top: 0px;
    vertical-align: middle;
    pointer-events: initial;
  }
  .el-edit-pencil {
    background-image: url('../../assets/icons/icon_edit_pencil.svg') !important;
  }
  .el-edit-pencil:hover {
    // background-color: #ebf4fd;
  }
}
</style>